if (!customElements.get('yc-quiz')) {
  class YcQuiz extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';
      this.abortController = new AbortController();
      this.handleClick = this.handleClick.bind(this);

      this.elements = {
        startView: this.querySelector('[data-quiz-start-view]'),
        mainView: this.querySelector('[data-quiz-main-view]'),
        progress: this.querySelector('[data-quiz-progress]'),
        progressCurrent: this.querySelector('[data-quiz-current-step]'),
        progressTotal: this.querySelector('[data-quiz-total-steps]'),
        progressTrack: this.querySelector('[data-quiz-progress-track]'),
        progressCounter: this.querySelector('[data-quiz-progress-counter]'),
        status: this.querySelector('[data-quiz-status]'),
        view: this.querySelector('[data-quiz-view]'),
      };

      this.bootstrap();
      this.addEventListener('click', this.handleClick, {
        signal: this.abortController.signal,
      });
      this.addEventListener('input', this.handleInput.bind(this), {
        signal: this.abortController.signal,
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    bootstrap() {
      const baseConfig = this.readConfig('base', this.dataset.baseConfig);
      const questionsConfig = this.readConfig('questions', this.dataset.questionsConfig);
      const deitiesConfig = this.readConfig('deities', this.dataset.deitiesConfig);

      const questions = this.normalizeQuestions(questionsConfig?.questions);
      const mantras = this.normalizeMantras(deitiesConfig?.mantras);

      this.state = {
        baseConfig,
        questions,
        mantras,
        mantraMap: new Map(mantras.map((mantra) => [mantra.key, mantra])),
        canStart: Boolean(baseConfig?.quizHandle && questions.length > 0 && mantras.length > 0),
        currentIndex: -1,
        isEmailCapture: false,
        answers: new Map(),
        questionStartedAt: 0,
        submitting: false,
        attemptId: '',
        latestSubmissionId: '',
        email: window.YcAppProxy?.getStorefrontIdentity?.().email || '',
        demoMode:
          this.dataset.designMode === 'true' || this.dataset.liveConfig !== 'true',
        trackingEnabled:
          this.dataset.designMode !== 'true' && this.dataset.liveConfig === 'true',
      };

      if (this.dataset.debugView) {
        this.state.attemptId = this.generateId('quiz-attempt');
        const debugView = this.dataset.debugView;
        
        if (debugView === 'email') {
          this.state.isEmailCapture = true;
          this.renderEmailCapture();
          return;
        }
        
        if (debugView === 'result') {
          this.renderResult(this.buildDemoResult());
          return;
        }
        
        if (debugView === 'question') {
          const stepIndex = parseInt(this.dataset.debugStep || '0', 10);
          this.state.currentIndex = isNaN(stepIndex) ? 0 : stepIndex;
          this.renderQuestion();
          return;
        }
      }

      this.renderIntro();
    }

    handleClick(event) {
      const target = event.target.closest('[data-action]');

      if (target && this.contains(target)) {
        if (target.matches(':disabled') || target.getAttribute('aria-disabled') === 'true') {
          return;
        }

        const action = target.getAttribute('data-action');

        if (action === 'start') {
          this.startQuiz();
          return;
        }

        if (action === 'restart') {
          this.startQuiz();
          return;
        }

        if (action === 'back') {
          this.goBack();
          return;
        }

        if (action === 'next') {
          this.goNext();
          return;
        }

        if (action === 'submit') {
          this.submitQuiz();
          return;
        }

        if (action === 'select-option') {
          const optionKey = target.getAttribute('data-option-key') || '';
          this.selectOption(optionKey);
          return;
        }
      }

      const productCard = event.target.closest('[data-track-product-click]');

      if (productCard && this.contains(productCard)) {
        this.trackProductClick(productCard);
      }
    }

    handleInput(event) {
      const target = event.target;
      if (target && target.matches('input[type="email"]') && this.contains(target)) {
        this.state.email = target.value.trim();
        const submitBtn = this.querySelector('[data-action="submit"]');
        if (submitBtn) {
          const isValid = this.state.email === '' || target.checkValidity();
          if (isValid) {
            submitBtn.removeAttribute('disabled');
          } else {
            submitBtn.setAttribute('disabled', 'disabled');
          }
        }
      }
    }

    startQuiz() {
      if (!this.state.canStart) {
        return;
      }

      this.clearStatus();
      this.state.currentIndex = 0;
      this.state.isEmailCapture = false;
      this.state.answers = new Map();
      this.state.submitting = false;
      this.state.attemptId = this.generateId('quiz-attempt');
      this.state.latestSubmissionId = '';
      this.state.email = window.YcAppProxy?.getStorefrontIdentity?.().email || '';
      this.markQuestionStart();
      this.queueTrackingEvent('quiz_started', {
        entityType: 'quiz',
        entityId: this.state.baseConfig.quizHandle,
        eventPayload: {
          quizHandle: this.state.baseConfig.quizHandle,
          configVersion: this.state.baseConfig.version,
          source: 'theme_section',
        },
      });
      this.renderQuestion();
    }

    goBack() {
      if (this.state.isEmailCapture) {
        this.state.isEmailCapture = false;
        this.clearStatus();
        this.renderQuestion();
        return;
      }

      if (this.state.currentIndex <= 0) {
        return;
      }

      this.persistCurrentAnswer(false);
      this.state.currentIndex -= 1;
      this.markQuestionStart();
      this.renderQuestion();
    }

    goNext() {
      if (!this.persistCurrentAnswer(true)) {
        this.setStatus('Please choose one answer before continuing.', 'error');
        return;
      }

      this.clearStatus();

      if (this.state.currentIndex === this.state.questions.length - 1) {
        this.state.isEmailCapture = true;
        this.renderEmailCapture();
        return;
      }

      this.state.currentIndex += 1;
      this.markQuestionStart();
      this.renderQuestion();
    }

    selectOption(optionKey) {
      const question = this.getCurrentQuestion();

      if (!question) {
        return;
      }

      const existing = this.state.answers.get(question.key);

      this.state.answers.set(question.key, {
        questionKey: question.key,
        optionKey,
        durationMs: existing?.durationMs || 0,
        tracked: existing?.tracked || false,
      });

      this.clearStatus();
      this.renderQuestion();
    }

    async submitQuiz() {
      if (this.state.submitting) {
        return;
      }

      const emailInput = this.querySelector('input[type="email"]');
      if (emailInput && this.state.email !== '' && !emailInput.checkValidity()) {
        this.setStatus('Please enter a valid email address.', 'error');
        return;
      }

      this.state.submitting = true;
      this.clearStatus();
      this.renderEmailCapture();

      try {
        const payload = this.buildSubmissionPayload();
        const result = this.state.demoMode
          ? this.buildDemoResult()
          : await this.submitToProxy(payload);

        this.state.latestSubmissionId = result.submissionId || '';
        this.queueTrackingEvent('quiz_completed', {
          entityType: 'quiz_submission',
          entityId: result.submissionId || this.state.attemptId,
          eventPayload: {
            quizHandle: result.quizHandle || this.state.baseConfig.quizHandle,
            configVersion: result.configVersion || this.state.baseConfig.version,
            submissionId: result.submissionId,
            innerMantra: result.innerMantra,
            outerMantra: result.outerMantra,
            source: this.state.demoMode ? 'theme_demo' : 'theme_section',
          },
        });

        if (payload.email) {
          this.queueTrackingEvent('email_submitted', {
            entityType: 'quiz_submission',
            entityId: result.submissionId || this.state.attemptId,
            eventPayload: {
              quizHandle: result.quizHandle || this.state.baseConfig.quizHandle,
              submissionId: result.submissionId,
              email: payload.email,
              source: this.state.demoMode ? 'theme_demo' : 'theme_section',
            },
          });
        }

        this.clearStatus();
        this.renderResult(result);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Unable to submit the quiz right now.';

        this.setStatus(message, 'error');
        this.state.submitting = false;
        this.renderEmailCapture();
      }
    }

    async submitToProxy(payload) {
      const helper = window.YcAppProxy;

      if (helper?.postJson) {
        const body = await helper.postJson(
          this.dataset.submitUrl || '/apps/kailashenergy/quiz/submissions',
          payload,
        );

        if (!body?.ok || !body?.data) {
          throw new Error(body?.error?.message || 'Unable to submit the quiz right now.');
        }

        return body.data;
      }

      const response = await fetch(this.dataset.submitUrl || '/apps/kailashenergy/quiz/submissions', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const body = await response.json().catch(() => null);

      if (!response.ok || !body?.ok || !body?.data) {
        throw new Error(body?.error?.message || 'Unable to submit the quiz right now.');
      }

      return body.data;
    }

    buildSubmissionPayload() {
      const baseConfig = this.state.baseConfig;
      const helper = window.YcAppProxy;
      const identityContext = helper?.readIdentityContext
        ? helper.readIdentityContext({
            moduleKey: 'yc-quiz',
            identity: {
              email: this.state.email,
            },
          })
        : null;
      const payload = helper?.buildModuleAppProxyPayload
        ? helper.buildModuleAppProxyPayload({
            moduleKey: 'yc-quiz',
            basePayload: {
              quizHandle: baseConfig.quizHandle,
              configVersion: baseConfig.version,
              configSnapshot: this.buildConfigSnapshot(),
              answers: this.state.questions.map((question) => {
                const answer = this.state.answers.get(question.key);

                return {
                  questionKey: question.key,
                  optionKey: answer?.optionKey || '',
                  durationMs: answer?.durationMs || 0,
                };
              }),
              attemptId: this.state.attemptId || this.generateId('quiz-attempt'),
            },
            identity: identityContext ?? {
              email: this.state.email,
            },
          })
        : {
        quizHandle: baseConfig.quizHandle,
        configVersion: baseConfig.version,
        configSnapshot: this.buildConfigSnapshot(),
        answers: this.state.questions.map((question) => {
          const answer = this.state.answers.get(question.key);

          return {
            questionKey: question.key,
            optionKey: answer?.optionKey || '',
            durationMs: answer?.durationMs || 0,
          };
        }),
        visitorId: this.getOrCreateStoredValue(
          this.dataset.visitorKey || 'yc-quiz-visitor-id',
          'visitor',
        ),
        sessionId: this.getOrCreateStoredSession(
          this.dataset.sessionKey || 'yc-quiz-session-id',
          'session',
        ),
        attemptId: this.state.attemptId || this.generateId('quiz-attempt'),
      };

      return payload;
    }

    buildConfigSnapshot() {
      const helper = window.YcAppProxy;

      if (helper?.buildConfigSnapshot) {
        return helper.buildConfigSnapshot({
          baseConfig: this.state.baseConfig,
          sections: {
            questions: {
              questions: this.state.questions,
            },
            deities: {
              mantras: this.state.mantras,
            },
          },
          inheritKeys: ['schemaVersion', 'quizHandle', 'version'],
        });
      }

      return {
        base: this.state.baseConfig,
        questions: {
          schemaVersion: this.state.baseConfig?.schemaVersion,
          quizHandle: this.state.baseConfig?.quizHandle,
          version: this.state.baseConfig?.version,
          questions: this.state.questions,
        },
        deities: {
          schemaVersion: this.state.baseConfig?.schemaVersion,
          quizHandle: this.state.baseConfig?.quizHandle,
          version: this.state.baseConfig?.version,
          mantras: this.state.mantras,
        },
      };
    }

    buildDemoResult() {
      const scores = {};
      const answers = [];

      this.state.questions.forEach((question) => {
        const answer = this.state.answers.get(question.key);
        const option = question.options.find((item) => item.key === answer?.optionKey);

        if (!answer || !option) {
          return;
        }

        Object.entries(option.score || {}).forEach(([key, value]) => {
          scores[key] = (scores[key] || 0) + Number(value || 0);
        });

        answers.push({
          questionKey: question.key,
          questionIndex: question.index,
          questionType: question.type,
          optionKey: option.key,
          optionLabel: option.label,
          optionValue: option.value,
          score: option.score,
          durationMs: answer.durationMs || 0,
        });
      });

      const rankedKeys = [...this.state.mantras]
        .sort((left, right) => {
          const scoreDelta = (scores[right.key] || 0) - (scores[left.key] || 0);

          if (scoreDelta !== 0) {
            return scoreDelta;
          }

          return Number(left.sortOrder || 0) - Number(right.sortOrder || 0);
        })
        .map((item) => item.key);

      const innerMantra = rankedKeys[0] || this.state.mantras[0]?.key || '';
      const outerMantra =
        rankedKeys[1] ||
        rankedKeys[0] ||
        this.state.mantras[1]?.key ||
        this.state.mantras[0]?.key ||
        '';

      return {
        submissionId: this.generateId('quiz-demo'),
        attemptId: this.state.attemptId,
        quizHandle: this.state.baseConfig.quizHandle,
        configVersion: this.state.baseConfig.version,
        innerMantra,
        outerMantra,
        scores,
        answers,
        recommendations: this.buildRecommendationsFromConfig(innerMantra, outerMantra),
      };
    }

    markQuestionStart() {
      this.state.questionStartedAt = performance.now();
    }

    persistCurrentAnswer(requireSelection) {
      const question = this.getCurrentQuestion();

      if (!question) {
        return true;
      }

      const existing = this.state.answers.get(question.key);

      if (!existing?.optionKey) {
        return !requireSelection;
      }

      const elapsed = Math.max(0, Math.round(performance.now() - this.state.questionStartedAt));
      const tracked = Boolean(existing?.tracked);

      this.state.answers.set(question.key, {
        questionKey: question.key,
        optionKey: existing.optionKey,
        durationMs: (existing.durationMs || 0) + elapsed,
        tracked,
      });

      if (!tracked) {
        const option = question.options.find((item) => item.key === existing.optionKey);
        const trackedAnswer = this.state.answers.get(question.key);

        this.queueTrackingEvent('question_answered', {
          entityType: 'question',
          entityId: question.key,
          eventPayload: {
            quizHandle: this.state.baseConfig.quizHandle,
            configVersion: this.state.baseConfig.version,
            questionKey: question.key,
            questionIndex: question.index,
            questionType: question.type,
            optionKey: existing.optionKey,
            optionLabel: option?.label,
            durationMs: trackedAnswer?.durationMs || 0,
            source: 'theme_section',
          },
        });

        this.state.answers.set(question.key, {
          ...trackedAnswer,
          tracked: true,
        });
      }

      return true;
    }

    getCurrentQuestion() {
      return this.state.questions[this.state.currentIndex] || null;
    }

    showView(viewName) {
      const isStartView = viewName === 'start';

      if (this.elements.startView) {
        this.elements.startView.hidden = !isStartView;
      }

      if (this.elements.mainView) {
        this.elements.mainView.hidden = isStartView;
      }

      this.dataset.activeView = viewName;
    }

    renderIntro() {
      const startButton = this.querySelector('[data-action="start"]');

      this.state.currentIndex = -1;
      this.state.isEmailCapture = false;
      this.clearStatus();
      this.toggleProgress(false);
      this.showView('start');

      if (startButton) {
        startButton.disabled = !this.state.canStart;
        startButton.setAttribute('aria-disabled', String(!this.state.canStart));
      }
    }

    renderQuestion() {
      const question = this.getCurrentQuestion();

      if (!question) {
        this.renderIntro();
        return;
      }

      this.showView('main');
      this.toggleProgress(true);

      const selectedOptionKey = this.state.answers.get(question.key)?.optionKey || '';

      this.updateProgress();

      this.elements.view.innerHTML = `
        <div class="yc-quiz__question-header">
          <p class="yc-quiz__kicker">${this.escapeHtml(this.state.baseConfig.title || 'Quiz')}</p>
          <h3 class="yc-quiz__question-title">${this.escapeHtml(question.title || '')}</h3>
        </div>
        ${
          question.type === 'scale_choice'
            ? this.renderScaleChoice(question, selectedOptionKey)
            : `
              <div class="yc-quiz__options yc-quiz__options--${this.escapeAttribute(question.type)}">
                ${question.options
                  .map((option) => this.renderOption(question, option, selectedOptionKey))
                  .join('')}
              </div>
            `
        }
        <div class="yc-quiz__actions">
          ${
            this.state.currentIndex >= 0
              ? `
                <button class="yc-quiz__button yc-quiz__button--back" type="button" data-action="back">
                  ${this.escapeHtml(this.dataset.backLabel || '← BACK')}
                </button>
              `
              : ''
          }
          <button
            class="yc-quiz__button"
            type="button"
            data-action="next"
            ${selectedOptionKey ? '' : 'disabled'}
          >
            ${this.escapeHtml(this.dataset.nextLabel || 'NEXT  →')}
          </button>
        </div>
      `;
    }

    renderEmailCapture() {
      this.toggleProgress(true);
      if (this.elements.progressCounter) {
        this.elements.progressCounter.hidden = true;
      }
      this.updateProgress();
      this.showView('main');

      const submitLabel = this.state.submitting
        ? 'Submitting...'
        : this.dataset.emailSubmitLabel || 'REVEAL MY MANTRAS  →';

      this.elements.view.innerHTML = `
        <div class="yc-quiz__email-capture">
          <p class="yc-quiz__kicker yc-quiz__email-kicker">${this.escapeHtml(this.dataset.emailEyebrow || 'ALMOST THERE')}</p>
          <h3 class="yc-quiz__email-heading">${this.escapeHtml(this.dataset.emailHeading || 'Leave your email to unlock your mantras')}</h3>
          
          <div class="yc-quiz__email-form">
            <input 
              type="email" 
              class="yc-quiz__email-input" 
              placeholder="${this.escapeHtml(this.dataset.emailPlaceholder || 'your@email.com')}"
              value="${this.escapeHtml(this.state.email)}"
              ${this.state.submitting ? 'disabled' : ''}
            >
            <p class="yc-quiz__email-privacy">${this.escapeHtml(this.dataset.emailPrivacy || 'We will never share your email')}</p>
          </div>

          <div class="yc-quiz__actions yc-quiz__actions--email">
            <button
              class="yc-quiz__button yc-quiz__button--submit"
              type="button"
              data-action="submit"
              ${this.state.submitting ? 'disabled' : ''}
            >
              ${this.escapeHtml(submitLabel)}
            </button>
          </div>

          <div class="yc-quiz__email-bottom-img-wrapper">
            <!-- JS will inject the start mobile image here -->
          </div>
        </div>
      `;

      const emailBottomImgWrapper = this.elements.view.querySelector('.yc-quiz__email-bottom-img-wrapper');
      const emailBottomImgTemplate = this.querySelector('.yc-quiz__email-bottom-img-template');
      const startMobileImgWrapper = this.elements.startView?.querySelector('.yc-quiz__start-mobile-img-wrapper');
      
      if (emailBottomImgWrapper) {
        if (emailBottomImgTemplate && emailBottomImgTemplate.innerHTML.trim() !== '') {
          emailBottomImgWrapper.innerHTML = emailBottomImgTemplate.innerHTML;
        } else if (startMobileImgWrapper) {
          emailBottomImgWrapper.innerHTML = startMobileImgWrapper.innerHTML;
        }
      }
    }

    renderScaleChoice(question, selectedOptionKey) {
      const selectedIndex = question.options.findIndex((opt) => opt.key === selectedOptionKey);
      const hasSelection = selectedIndex >= 0;
      const percent = hasSelection
        ? (selectedIndex / Math.max(1, question.options.length - 1)) * 100
        : 50;
      const selectedOption = hasSelection ? question.options[selectedIndex] : null;
      const firstOptionLabel = String(question.options[0]?.label || '').trim();
      const lastOptionLabel = String(
        question.options[question.options.length - 1]?.label || '',
      ).trim();
      const selectedOptionText = hasSelection
        ? this.getScaleOptionDisplayText(question, selectedOption, selectedIndex)
        : '';

      return `
        <div class="yc-quiz__scale-container">
          <div class="yc-quiz__scale-track-container">
            <div class="yc-quiz__scale-track-line"></div>
            <div class="yc-quiz__scale-track-fill" style="width: ${hasSelection ? percent : 0}%; opacity: ${hasSelection ? 1 : 0};"></div>
            <div class="yc-quiz__scale-track-thumb" style="left: ${hasSelection ? percent : 50}%; opacity: ${hasSelection ? 1 : 0};"></div>
          </div>
          <div class="yc-quiz__scale-labels">
            <span>${this.escapeHtml(firstOptionLabel || question.scale?.minLabel || 'Unknown')}</span>
            <span>${this.escapeHtml(lastOptionLabel || question.scale?.maxLabel || 'Unknown')}</span>
          </div>
          <div class="yc-quiz__options yc-quiz__options--scale_choice">
            ${question.options
              .map((option, optionIndex) => {
                const isSelected = selectedOptionKey === option.key;
                const optionText = this.getScaleOptionDisplayText(question, option, optionIndex);
                return `
                  <button
                    class="yc-quiz__option ${isSelected ? 'is-selected' : ''}"
                    type="button"
                    data-action="select-option"
                    data-option-key="${this.escapeAttribute(option.key)}"
                    aria-label="${this.escapeAttribute(optionText || option.label || option.key)}"
                  >
                    <span class="yc-quiz__option-dot"></span>
                  </button>
                `;
              })
              .join('')}
          </div>
          <div class="yc-quiz__scale-selected-text ${hasSelection ? 'is-visible' : ''}">
            ${selectedOptionText ? this.escapeHtml(selectedOptionText) : '&nbsp;'}
          </div>
        </div>
      `;
    }

    getScaleOptionDisplayText(question, option, optionIndex) {
      if (!option) {
        return '';
      }

      const description = String(option.description || '').trim();
      if (description) {
        return description;
      }

      const label = String(option.label || '').trim();
      if (label && !/^\d+(\.\d+)?$/.test(label)) {
        return label;
      }

      const options = Array.isArray(question?.options) ? question.options : [];
      const minLabel = String(question?.scale?.minLabel || '').trim();
      const maxLabel = String(question?.scale?.maxLabel || '').trim();

      if (!minLabel && !maxLabel) {
        return label;
      }

      if (options.length <= 1) {
        return minLabel || maxLabel || label;
      }

      if (optionIndex === 0) {
        return minLabel || label;
      }

      if (optionIndex === options.length - 1) {
        return maxLabel || label;
      }

      const midpoint = (options.length - 1) / 2;

      if (optionIndex < midpoint) {
        return minLabel ? `Closer to ${minLabel}` : label;
      }

      if (optionIndex > midpoint) {
        return maxLabel ? `Closer to ${maxLabel}` : label;
      }

      if (minLabel && maxLabel) {
        return `${minLabel} / ${maxLabel}`;
      }

      return minLabel || maxLabel || label;
    }

    renderOption(question, option, selectedOptionKey) {
      const isSelected = selectedOptionKey === option.key;
      const classes = ['yc-quiz__option'];

      if (isSelected) {
        classes.push('is-selected');
      }

      const imageMarkup =
        question.type === 'card_choice' && option.image
          ? `<img class="yc-quiz__option-image" src="${this.escapeAttribute(
              option.image,
            )}" alt="${this.escapeAttribute(option.label || '')}">`
          : '';

      const caption =
        typeof option.value === 'number'
          ? `<span class="yc-quiz__option-caption">${this.escapeHtml(String(option.value))}</span>`
          : '';

      const description = option.description && question.type !== 'text_choice' && question.type !== 'card_choice'
        ? `<p class="yc-quiz__option-description">${this.escapeHtml(option.description)}</p>`
        : '';

      return `
        <button
          class="${classes.join(' ')}"
          type="button"
          data-action="select-option"
          data-option-key="${this.escapeAttribute(option.key)}"
        >
          ${imageMarkup}
          <div class="yc-quiz__option-title-row">
            <span class="yc-quiz__option-title">${this.escapeHtml(option.label || '')}</span>
            ${caption || '<span class="yc-quiz__option-check"></span>'}
          </div>
          ${description}
        </button>
      `;
    }

    renderResult(result) {
      this.state.submitting = false;
      this.state.currentIndex = -1;
      this.state.isEmailCapture = false;
      this.toggleProgress(false);
      this.showView('main');

      const inner = this.state.mantraMap.get(result.innerMantra);
      const outer = this.state.mantraMap.get(result.outerMantra);

      // Collect unique tags
      const allTags = new Set();
      if (inner?.displayTags) inner.displayTags.forEach(t => allTags.add(t.label));
      if (outer?.displayTags) outer.displayTags.forEach(t => allTags.add(t.label));
      if (inner?.element) allTags.add(`${inner.element} Element`);
      const tagsHtml = Array.from(allTags).map(tag => `<span class="yc-quiz__result-tag">${this.escapeHtml(tag)}</span>`).join('');

      const responseRecommendations = Array.isArray(result.recommendations)
        ? result.recommendations.filter(
            (section) => Array.isArray(section?.products) && section.products.length > 0,
          )
        : [];
      const recommendations = responseRecommendations.length
        ? responseRecommendations.map(section => {
            let subtitle = section.subtitle;
            let title = section.title;
            if (section.sectionType === 'inner') {
              subtitle = subtitle || this.dataset.recommendationInnerSubtitle;
              title = `Inner Mantra · ${inner?.displayName || inner?.name || ''}`;
            } else if (section.sectionType === 'outer') {
              subtitle = subtitle || this.dataset.recommendationOuterSubtitle;
              title = `Outer Mantra · ${outer?.displayName || outer?.name || ''}`;
            } else if (section.sectionType === 'element') {
              subtitle = subtitle || this.dataset.recommendationElementSubtitle;
              const elementName = inner?.element || '';
              const capitalizedElement = elementName ? elementName.charAt(0).toUpperCase() + elementName.slice(1) : '';
              title = `${capitalizedElement} Element · ${inner?.element || 'Unknown'}`;
            }
            return { ...section, title, subtitle };
          })
        : this.buildRecommendationsFromConfig(result.innerMantra, result.outerMantra);

      const exploreLink = inner?.relatedCollection?.handle
        ? `/collections/${inner.relatedCollection.handle}`
        : this.dataset.exploreLink || '/collections/all';
        
      const exploreLabel = inner?.relatedCollection?.title
        ? `EXPLORE ${inner.relatedCollection.title.toUpperCase()} →`
        : this.dataset.exploreLabel || 'EXPLORE THE COLLECTION →';

      this.elements.view.innerHTML = `
        <div class="yc-quiz__result-header">
          <p class="yc-quiz__kicker">${this.escapeHtml(this.dataset.resultEyebrow || 'Your Mantra Reading')}</p>
          <h3 class="yc-quiz__result-title">${this.escapeHtml(this.dataset.resultHeading || 'You hold two sounds at once')}</h3>
          <div class="yc-quiz__result-tags">${tagsHtml}</div>
        </div>
        
        <div class="yc-quiz__mantra-hero-grid">
          ${this.renderMantraHero('Inner', inner)}
          ${this.renderMantraHero('Outer', outer)}
        </div>

        <hr class="yc-quiz__divider">

        <div class="yc-quiz__mantra-desc-grid">
          ${this.renderMantraDesc('Your Inner Mantra', inner, 'inner')}
          ${this.renderMantraDesc('Your Outer Mantra', outer, 'outer')}
        </div>

        ${
          recommendations.length
            ? `
              <div class="yc-quiz__recommendations-wrapper">
                <div class="yc-quiz__recommendations-header">
                  <h4 class="yc-quiz__recommendations-title">Your energy called this to you.</h4>
                </div>
                ${recommendations.map((section) => this.renderRecommendationSection(section)).join('')}
              </div>
            `
            : ''
        }

        <div class="yc-quiz__result-footer">
          <p>${this.escapeHtml(this.dataset.resultFooter || '')}</p>
          <div class="yc-quiz__actions yc-quiz__actions--result">
            <a href="${this.escapeAttribute(exploreLink)}" class="yc-quiz__button yc-quiz__button--explore">${this.escapeHtml(exploreLabel)}</a>
            <button class="yc-quiz__button yc-quiz__button--secondary yc-quiz__button--retake" type="button" data-action="restart">
              ${this.escapeHtml(this.dataset.restartLabel || 'RETAKE')}
            </button>
          </div>
        </div>
      `;
    }

    renderMantraHero(label, mantra) {
      if (!mantra) return '';
      const metaItems = [mantra.element, mantra.emotion, mantra.color]
        .map((value) => String(value || '').trim())
        .filter(Boolean);
      const metaString = metaItems.join(' · ');
      const placeholderSvg = this.dataset.placeholderMantra || '<div class="yc-quiz__mantra-hero-img yc-quiz__placeholder-img"></div>';
      const imageMarkup = mantra.image
        ? `<img class="yc-quiz__mantra-hero-img" src="${this.escapeAttribute(mantra.image)}" alt="">`
        : placeholderSvg;
      
      const displayLabel = `${label} Mantra · ${mantra.displayName || mantra.name || ''}`;

      return `
        <div class="yc-quiz__mantra-hero-card">
          ${imageMarkup}
          <p class="yc-quiz__mantra-hero-label">${this.escapeHtml(displayLabel)}</p>
          <p class="yc-quiz__mantra-hero-tags">${this.escapeHtml(metaString)}</p>
        </div>
      `;
    }

    renderMantraDesc(title, mantra, role) {
      if (!mantra) return '';
      const metaString = [mantra.element, mantra.emotion]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join(' · ');
      const description = this.getMantraDisplayDescription(mantra, role);
      
      return `
        <div class="yc-quiz__mantra-desc-card">
          <h3 class="yc-quiz__mantra-desc-title">${this.escapeHtml(title)}</h3>
          <p class="yc-quiz__mantra-desc-tags">${this.escapeHtml(metaString)}</p>
          <p class="yc-quiz__mantra-desc-text">${this.escapeHtml(description)}</p>
        </div>
      `;
    }

    getMantraDisplayDescription(mantra, role) {
      if (!mantra || typeof mantra !== 'object') {
        return '';
      }

      const fieldName =
        role === 'outer' ? 'outerDisplayDescription' : 'innerDisplayDescription';

      return String(mantra[fieldName] || '').trim();
    }

    renderScoreItem(item) {
      return `
        <div class="yc-quiz__score-item">
          <div class="yc-quiz__score-row">
            <span class="yc-quiz__score-label">${this.escapeHtml(item.label)}</span>
            <span>${this.escapeHtml(String(item.score))}</span>
          </div>
          <div class="yc-quiz__score-track" aria-hidden="true">
            <span class="yc-quiz__score-fill" style="width:${item.percent}%"></span>
          </div>
        </div>
      `;
    }

    renderRecommendationSection(section) {
      if (!section || !Array.isArray(section.products) || section.products.length === 0) {
        return '';
      }

      const products = [...section.products];
      while (products.length > 0 && products.length < 3) {
        products.push({
          isPlaceholder: true,
          title: this.dataset.previewProductTitle || 'Preview product',
          reason: this.dataset.previewProductReason || 'Coming soon',
        });
      }

      return `
        <div class="yc-quiz__recommendation-section">
          <div class="yc-quiz__recommendation-header">
            <h4 class="yc-quiz__recommendation-title">${this.escapeHtml(section.title || '')}</h4>
            ${
              section.subtitle
                ? `<p class="yc-quiz__recommendation-subtitle">${this.escapeHtml(section.subtitle)}</p>`
                : ''
            }
          </div>
          <div class="yc-quiz__recommendation-products">
            ${products.map((product) => this.renderProductCard(product)).join('')}
          </div>
        </div>
      `;
    }

    renderProductCard(product) {
      const hasHandle = typeof product?.handle === 'string' && product.handle;
      const tagName = hasHandle ? 'a' : 'article';
      const href = hasHandle ? `/products/${product.handle}` : '';
      const attributes = hasHandle
        ? `href="${this.escapeAttribute(href)}" data-track-product-click="true" data-product-handle="${this.escapeAttribute(
            product.handle,
          )}" data-product-gid="${this.escapeAttribute(product.productGid || '')}" data-product-title="${this.escapeAttribute(
            product.title || '',
          )}"`
        : 'role="article"';
      const placeholderSvg = this.dataset.placeholderProduct || '<div class="yc-quiz__product-image yc-quiz__placeholder-img"></div>';
      const imageMarkup = product?.image
        ? `<div class="yc-quiz__product-image-wrapper"><img class="yc-quiz__product-image" src="${this.escapeAttribute(
            product.image,
          )}" alt="${this.escapeAttribute(product.title || 'Recommended product')}"></div>`
        : `<div class="yc-quiz__product-image-wrapper">${placeholderSvg}</div>`;

      return `
        <${tagName} class="yc-quiz__product-card" ${attributes}>
          ${imageMarkup}
          <div class="yc-quiz__product-meta">
            <div class="yc-quiz__product-text-wrapper">
              &nbsp;
              <p class="yc-quiz__product-title">${this.escapeHtml(
                product?.title ||
                  this.formatProductFallbackLabel(product?.productGid) ||
                  'Recommended product',
              )}</p>
            </div>
            ${
              product?.price
                ? `<div class="yc-quiz__product-text-wrapper">&nbsp;<p class="yc-quiz__product-price">${this.escapeHtml(product.price)}</p></div>`
                : ''
            }
            ${
              product?.reason
                ? `<div class="yc-quiz__product-text-wrapper">&nbsp;<p class="yc-quiz__product-reason">${this.escapeHtml(product.reason)}</p></div>`
                : ''
            }
          </div>
          <span class="yc-quiz__product-link">${this.escapeHtml(this.dataset.productLinkLabel || 'SHOP NOW →')}</span>
        </${tagName}>
      `;
    }

    buildRecommendationsFromConfig(innerMantraKey, outerMantraKey) {
      const innerMantra = this.state.mantraMap.get(innerMantraKey);
      const outerMantra = this.state.mantraMap.get(outerMantraKey);
      const sections = [];

      if (innerMantra?.recommendedProducts?.length) {
        sections.push({
          sectionType: 'inner',
          title: `Inner Mantra · ${innerMantra.displayName || innerMantra.name}`,
          subtitle:
            this.dataset.recommendationInnerSubtitle ||
            'For your private practice — pieces that match your inner frequency',
          products: innerMantra.recommendedProducts,
        });
      }

      if (outerMantra?.recommendedProducts?.length) {
        sections.push({
          sectionType: 'outer',
          title: `Outer Mantra · ${outerMantra.displayName || outerMantra.name}`,
          subtitle:
            this.dataset.recommendationOuterSubtitle ||
            'For how you move through the world — pieces that show your outer energy',
          products: outerMantra.recommendedProducts,
        });
      }

      if (innerMantra?.elementProducts?.length) {
        const elementName = innerMantra.element || '';
        const capitalizedElement = elementName ? elementName.charAt(0).toUpperCase() + elementName.slice(1) : '';
        sections.push({
          sectionType: 'element',
          title: `${capitalizedElement} Element · ${innerMantra.element || 'Unknown'}`,
          subtitle:
            this.dataset.recommendationElementSubtitle ||
            'Materials drawn to your element',
          products: innerMantra.elementProducts,
        });
      }

      return sections;
    }

    trackProductClick(productCard) {
      this.queueTrackingEvent('product_clicked', {
        entityType: 'quiz_submission',
        entityId: this.state.latestSubmissionId || this.state.attemptId || this.state.baseConfig.quizHandle,
        eventPayload: {
          quizHandle: this.state.baseConfig.quizHandle,
          submissionId: this.state.latestSubmissionId || undefined,
          productHandle: productCard.getAttribute('data-product-handle') || undefined,
          productGid: productCard.getAttribute('data-product-gid') || undefined,
          productTitle: productCard.getAttribute('data-product-title') || undefined,
          source: 'theme_section',
        },
      });
    }

    toggleProgress(visible) {
      if (this.elements.progress) {
        this.elements.progress.hidden = !visible;
      }
      if (this.elements.progressCounter) {
        this.elements.progressCounter.hidden = !visible;
      }
    }

    updateProgress() {
      const total = this.state.questions.length;
      const current = this.state.currentIndex + 1;

      if (this.elements.progressCurrent) {
        this.elements.progressCurrent.textContent = String(current).padStart(2, '0');
      }

      if (this.elements.progressTotal) {
        this.elements.progressTotal.textContent = String(total).padStart(2, '0');
      }

      if (this.elements.progressTrack) {
        this.elements.progressTrack.innerHTML = Array.from({ length: total }, (_, i) => {
          const isActive = i < current;
          return `<span class="yc-quiz__progress-segment ${isActive ? 'is-active' : ''}"></span>`;
        }).join('');
      }
    }

    buildScoreRanking(scores) {
      const entries = [...this.state.mantras]
        .map((mantra) => ({
          key: mantra.key,
          label: mantra.displayName || mantra.name || mantra.key,
          score: Number(scores?.[mantra.key] || 0),
        }))
        .sort((left, right) => right.score - left.score)
        .slice(0, 4);

      const maxScore = entries[0]?.score || 1;

      return entries.map((item) => ({
        ...item,
        percent: Math.max(8, Math.round((item.score / maxScore) * 100)),
      }));
    }

    normalizeQuestions(value) {
      if (!Array.isArray(value)) {
        return [];
      }

      return [...value]
        .filter((item) => item && typeof item === 'object')
        .sort((left, right) => Number(left.index || 0) - Number(right.index || 0))
        .map((question) => {
          const options = Array.isArray(question.options)
            ? [...question.options]
                .filter(
                  (option) =>
                    option &&
                    typeof option === 'object' &&
                    String(option.key || '').trim() !== '',
                )
                .sort((left, right) => Number(left.sortOrder || 0) - Number(right.sortOrder || 0))
            : [];

          return {
            ...question,
            key: String(question.key || '').trim(),
            options,
          };
        })
        .filter(
          (question) =>
            question.key !== '' &&
            String(question.title || '').trim() !== '' &&
            question.options.length > 0,
        );
    }

    normalizeMantras(value) {
      if (!Array.isArray(value)) {
        return [];
      }

      return [...value]
        .filter((item) => item && typeof item === 'object')
        .sort((left, right) => Number(left.sortOrder || 0) - Number(right.sortOrder || 0))
        .map((mantra) => ({
          ...mantra,
          recommendedProducts: Array.isArray(mantra.recommendedProducts)
            ? mantra.recommendedProducts
            : [],
          elementProducts: Array.isArray(mantra.elementProducts)
            ? mantra.elementProducts
            : [],
          displayTags: Array.isArray(mantra.displayTags) ? mantra.displayTags : [],
        }));
    }

    readConfig(key, fallbackValue) {
      const inlineConfig = this.querySelector(`[data-quiz-config="${key}"]`);
      return this.parseJsonConfig(inlineConfig?.textContent || fallbackValue);
    }

    parseJsonConfig(value) {
      if (!value) {
        return null;
      }

      try {
        let parsed = JSON.parse(value);

        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
        }

        return parsed;
      } catch (error) {
        return null;
      }
    }

    formatProductFallbackLabel(productGid) {
      if (!productGid) {
        return '';
      }

      const parts = String(productGid).split('/');
      return parts[parts.length - 1] || '';
    }

    getOrCreateStoredValue(storageKey, prefix) {
      const helper = window.YcAppProxy;

      if (helper?.getOrCreateStoredValue) {
        return helper.getOrCreateStoredValue(window.localStorage, storageKey, prefix);
      }

      try {
        const currentValue = window.localStorage.getItem(storageKey);

        if (currentValue) {
          return currentValue;
        }

        const nextValue = this.generateId(prefix);
        window.localStorage.setItem(storageKey, nextValue);
        return nextValue;
      } catch (error) {
        return this.generateId(prefix);
      }
    }

    getOrCreateStoredSession(storageKey, prefix) {
      const helper = window.YcAppProxy;

      if (helper?.getOrCreateStoredValue) {
        return helper.getOrCreateStoredValue(window.sessionStorage, storageKey, prefix);
      }

      try {
        const currentValue = window.sessionStorage.getItem(storageKey);

        if (currentValue) {
          return currentValue;
        }

        const nextValue = this.generateId(prefix);
        window.sessionStorage.setItem(storageKey, nextValue);
        return nextValue;
      } catch (error) {
        return this.generateId(prefix);
      }
    }

    generateId(prefix) {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return `${prefix}-${window.crypto.randomUUID()}`;
      }

      return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    buildTrackingPayload(eventName, detail = {}) {
      const helper = window.YcAppProxy;
      const identityContext = helper?.readIdentityContext
        ? helper.readIdentityContext({
            moduleKey: 'yc-quiz',
            identity: {
              email: this.state.email,
            },
          })
        : {
            visitorId: this.getOrCreateStoredValue(
              this.dataset.visitorKey || 'yc-quiz-visitor-id',
              'visitor',
            ),
            sessionId: this.getOrCreateStoredSession(
              this.dataset.sessionKey || 'yc-quiz-session-id',
              'session',
            ),
            email: this.state.email || undefined,
          };
      const visitorId = identityContext.visitorId;
      const sessionId = identityContext.sessionId;

      if (helper?.buildTrackingPayload) {
        return helper.buildTrackingPayload({
          appModule: 'quiz',
          eventName,
          visitorId,
          sessionId,
          occurredAt: new Date().toISOString(),
          identity: {
            customerId: identityContext.customerId,
            customerGid: identityContext.customerGid,
            email: identityContext.email,
            phone: identityContext.phone,
          },
          page: {
            pageUrl: window.location.href,
            referrer: document.referrer || undefined,
            userAgent: navigator.userAgent,
          },
          detail,
        });
      }

      return {
        appModule: 'quiz',
        eventName,
        visitorId,
        sessionId,
        customerId: identityContext.customerId,
        customerGid: identityContext.customerGid,
        email: identityContext.email,
        pageUrl: window.location.href,
        referrer: document.referrer || undefined,
        userAgent: navigator.userAgent,
        occurredAt: new Date().toISOString(),
        ...detail,
      };
    }

    queueTrackingEvent(eventName, detail) {
      if (!this.state.trackingEnabled) {
        return;
      }

      const helper = window.YcAppProxy;
      const trackingUrl = this.dataset.trackingUrl || '/apps/kailashenergy/tracking/events';
      const payload = this.buildTrackingPayload(eventName, detail);

      if (helper?.trackEvent) {
        helper
          .trackEvent({
            moduleKey: 'yc-quiz',
            appModule: 'quiz',
            eventName,
            identity: {
              email: this.state.email,
            },
            detail,
            page: {
              keepalive: true,
            },
            url: trackingUrl,
          })
          .catch(() => {});
        return;
      }

      fetch(trackingUrl, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }).catch(() => {});
    }

    setStatus(message, tone) {
      if (!this.elements.status) {
        return;
      }

      this.elements.status.hidden = !message;
      this.elements.status.textContent = message || '';
      this.elements.status.classList.toggle('is-error', tone === 'error');
      this.elements.status.classList.toggle('is-success', tone === 'success');
    }

    clearStatus() {
      this.setStatus('', '');
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value).replace(/`/g, '&#96;');
    }
  }

  customElements.define('yc-quiz', YcQuiz);
}
